// The explainer the two free MRZ tools share, and the definition of a
// machine-readable zone this whole site answers with.
//
// The two tools were 101 and 108 words of prose around a form. A tool page with
// no explanation ranks for nothing and teaches nobody: the reader who arrives
// having typed "mrz check digit algorithm" wants the algorithm, and the one who
// arrives with a zone that will not validate wants to know which of the digits
// is the one that failed and why.
//
// The worked example is COMPUTED, not typed. An explainer that retyped the
// products of the 7-3-1 weighting would be a second implementation of
// $lib/seo/mrz to keep true, and the first thing a reader checking it with the
// tool on the same page would find wrong.

import { type CheckDigitWorking, checkDigitSteps } from "./mrz";
import { MRZ_PARSER_TEXT_EN, type MrzParserText, parseMrz } from "./mrz-parser";
import { SPECIMEN_DOCUMENT_NUMBER, specimenFormatZones } from "./zones";

/**
 * What a machine-readable zone is, in one paragraph of 40–55 words.
 *
 * That is the shape a definition question is answered in, and a longer answer
 * with the definition buried in the middle forfeits the box however good the
 * prose is. One wording, used wherever this site answers the question, so two
 * pages cannot define the same term two ways.
 */
export const MRZ_DEFINITION =
  "A machine-readable zone is the block of fixed-width lines at the foot of a passport " +
  "or identity card, standardised by ICAO Doc 9303. It encodes the document number, " +
  "issuing state, name, nationality, date of birth, sex and expiry date, each at a " +
  "fixed offset, with check digits that verify the reading.";

/** The numbered steps that compute a check digit, in the order they are applied. */
export const CHECK_DIGIT_STEPS: readonly string[] = [
  "Take the field the digit protects – the document number, the date of birth, the date of " +
    "expiry, the personal number, or the whole composite range.",
  "Give every character its value: a digit counts as itself, a letter as its position in the " +
    "alphabet plus nine (A = 10, Z = 35), and the filler < as zero.",
  "Multiply each value by its position weight. The weights repeat 7, 3, 1 across the field, " +
    "starting at 7 on the first character.",
  "Add the products together.",
  "The check digit is that sum modulo 10 – the last digit of the total.",
];

/**
 * The same arithmetic worked through on the specimen's own passport number, so
 * a reader can follow it character by character and then paste the zone into
 * the checker above and watch it come out the same.
 */
export const WORKED_EXAMPLE: CheckDigitWorking = checkDigitSteps(SPECIMEN_DOCUMENT_NUMBER);

/** What the worked example is of, said before the table. */
export const WORKED_EXAMPLE_LEAD =
  `Worked through on the specimen's passport number ${SPECIMEN_DOCUMENT_NUMBER} – an invented ` +
  "document belonging to nobody:";

/** The composite digit, and why it is the one an implementation gets wrong. */
export const COMPOSITE_DIGIT =
  "One more digit covers most of the zone at once: the composite. It is computed the same way, " +
  "over a range that skips the fields no digit protects – and in TD1 that range spans two " +
  "lines, which is where a hand-written parser usually goes wrong. A zone whose field digits " +
  "all pass and whose composite fails has been transcribed correctly in pieces and assembled " +
  "wrongly.";

/** What no check digit covers, which is the more useful half of the answer. */
export const CHECK_DIGIT_GAP =
  "No check digit covers the name, and none covers the issuing state. A misread in either is " +
  "silent: the arithmetic still balances. That is why reading the printed side of the document " +
  "as well, and comparing the two readings, catches what the digits cannot.";

/** One visible question and its answer. No structured markup accompanies it. */
export interface ToolQuestion {
  readonly id: string;
  readonly question: string;
  readonly answer: string;
}

/**
 * The checker's questions, in the wording readers actually use.
 *
 * The third one is answered from what the code does rather than from what would
 * sound best: the page is prerendered, the parsing runs in the browser, and the
 * one request the page makes after it loads carries nothing that was typed into
 * it.
 */
export const CHECKER_FAQ: readonly ToolQuestion[] = [
  {
    id: "what-is-an-mrz",
    question: "What is an MRZ?",
    answer: MRZ_DEFINITION,
  },
  {
    id: "formats-supported",
    question: "Which formats does this tool support?",
    answer:
      "All three ICAO 9303 layouts: TD3, the two lines of 44 a passport carries; TD2, two " +
      "lines of 36; and TD1, the three lines of 30 on an identity card or residence permit. It " +
      "also reads the two visa layouts, MRV-A and MRV-B, which have no composite digit. The " +
      "format is detected from the number of lines and their length, and every check digit " +
      "the format has is recomputed.",
  },
  {
    id: "why-a-digit-fails",
    question: "Why does a check digit fail?",
    answer:
      "Almost always because one character in the field it protects was misread or mistyped: " +
      "an O for a 0, a B for an 8, a filler lost at the end of a line. The table shows the " +
      "digit the zone prints beside the one the data gives, and the field the digit covers. " +
      "When a field digit passes and the composite fails, look at the characters between the " +
      "fields – the composite also covers the field digits themselves.",
  },
  {
    id: "read-a-photo",
    question: "Can it read the zone from a photo?",
    answer:
      "No. This page reads text you paste, and nothing leaves your browser. Reading the zone " +
      "and the printed page off a photo is what the recognition API does: one HTTP call with " +
      "the image, and the fields come back as JSON with the same check digits already verified.",
  },
  {
    id: "store-my-data",
    question: "Do you store any of my data?",
    answer:
      "No. The zone you paste is parsed by code running in your own browser: nothing is " +
      "uploaded, and no request carrying it is made. This page is a static file, and the only " +
      "request it makes after it loads is to ask whether there is a service notice to show at " +
      "the top – a call that carries nothing you typed. The recognition API is a separate " +
      "thing you have to call deliberately.",
  },
];

/** Where one field or check digit sits in each layout, e.g. "line 2, 1–9". */
export interface FieldPositionRow {
  readonly label: string;
  readonly td3: string;
  readonly td2: string;
  readonly td1: string;
}

/** The row labels of the field positions table, in one language. */
export interface FieldPositionLabels {
  readonly documentCode: string;
  readonly issuingState: string;
  readonly name: string;
  readonly documentNumber: string;
  readonly nationality: string;
  readonly dateOfBirth: string;
  readonly sex: string;
  readonly expiryDate: string;
  readonly personalNumber: string;
  /** The row under each protected field, for the digit that follows it. */
  readonly itsCheckDigit: string;
  readonly composite: string;
}

export const FIELD_POSITION_LABELS_EN: FieldPositionLabels = {
  documentCode: "Document code",
  issuingState: "Issuing state",
  name: "Name",
  documentNumber: "Document number",
  nationality: "Nationality",
  dateOfBirth: "Date of birth",
  sex: "Sex",
  expiryDate: "Expiry date",
  personalNumber: "Personal number",
  itsCheckDigit: "its check digit",
  composite: "Composite check digit",
};

/**
 * The field-by-field layout of the three formats, as the parser reads them.
 *
 * COMPUTED by parsing the site's own specimen zones, not typed: a reference
 * table typed beside the parser would be a second statement of the layout, and
 * the one that drifted would be the one a reader trusted. A page in another
 * language passes its row labels and the parser's words, so the positions come
 * out in that language from the same parse; with neither, the table is English.
 */
export function fieldPositions(
  text: { labels: FieldPositionLabels; parser: MrzParserText } = {
    labels: FIELD_POSITION_LABELS_EN,
    parser: MRZ_PARSER_TEXT_EN,
  },
): readonly FieldPositionRow[] {
  const byFormat = new Map(
    specimenFormatZones().map((zone) => [
      zone.format,
      parseMrz(zone.lines.join("\n"), { text: text.parser }),
    ]),
  );
  const where = (format: "TD1" | "TD2" | "TD3", key: string, digit: boolean) => {
    const result = byFormat.get(format);
    const found = digit
      ? result?.checks.find((c) => c.key === key)
      : result?.fields.find((f) => f.key === key);
    return found?.where ?? "–";
  };
  const l = text.labels;
  const rows: [string, string, boolean][] = [
    [l.documentCode, "documentCode", false],
    [l.issuingState, "issuingState", false],
    [l.name, "surname", false],
    [l.documentNumber, "documentNumber", false],
    [l.itsCheckDigit, "documentNumber", true],
    [l.nationality, "nationality", false],
    [l.dateOfBirth, "dateOfBirth", false],
    [l.itsCheckDigit, "dateOfBirth", true],
    [l.sex, "sex", false],
    [l.expiryDate, "expiryDate", false],
    [l.itsCheckDigit, "expiryDate", true],
    [l.personalNumber, "personalNumber", false],
    [l.itsCheckDigit, "personalNumber", true],
    [l.composite, "composite", true],
  ];
  return rows.map(([label, key, digit]) => ({
    label,
    td3: where("TD3", key, digit),
    td2: where("TD2", key, digit),
    td1: where("TD1", key, digit),
  }));
}

/** The generator's questions. Same first question, a different tool for the others. */
export const GENERATOR_FAQ: readonly ToolQuestion[] = [
  {
    id: "what-is-an-mrz",
    question: "What is an MRZ?",
    answer: MRZ_DEFINITION,
  },
  {
    id: "formats-supported",
    question: "Which formats does this tool support?",
    answer:
      "This generator builds TD3 – the two lines of 44 characters a passport carries – with " +
      "every check digit computed from the values you enter, including the composite. The " +
      "format explainer sets out TD1 and TD2 as well, with a worked example of each.",
  },
  {
    id: "store-my-data",
    question: "Do you store any of my data?",
    answer:
      "No. The zone is built by code running in your own browser and never leaves the page: " +
      "nothing is uploaded and no request carrying your values is made. This page is a static " +
      "file, and the only request it makes after it loads is to ask whether there is a service " +
      "notice to show at the top. Use invented values anyway – a generated zone is for test " +
      "fixtures, and real personal data does not belong in one.",
  },
];

/** Where the explainer sends a reader next, from each tool. */
export interface ExplainerLink {
  readonly href: string;
  readonly label: string;
  readonly blurb: string;
}

export const EXPLAINER_LINKS: readonly ExplainerLink[] = [
  {
    href: "/mrz-formats",
    label: "TD1, TD2 and TD3",
    blurb:
      "the three layouts, their line and character counts, and where the standard defines each.",
  },
  {
    href: "/",
    label: "Passport OCR API",
    blurb: "the printed side as well as the encoded one, read from a photo in one request.",
  },
  {
    href: "/free-passport-ocr-api",
    label: "Free passport OCR API",
    blurb: "the first calls with no account, as a single curl command.",
  },
  {
    href: "/passport-ocr",
    label: "Passport OCR by country",
    blurb: "the issuing states this API reads, each with a specimen zone of its own.",
  },
];
